import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  CircleOff,
  Headphones,
  KeyRound,
  LoaderCircle,
  PhoneIncoming,
  PhoneOutgoing,
  Save,
  Search,
  Users,
} from 'lucide-react';
import { useAuth } from '../../auth';
import { userHasRole } from '../../roles';
import { telephonyApi } from '../telephonyApi';
import { sipCredentialActionLabel, sipCredentialRegenerationAvailability } from '../SipCredentialRegeneration';
import type { BrowserPhoneUser } from '../types';

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    AVAILABLE: 'Доступен',
    BUSY: 'Занят',
    CALL: 'Разговор',
    POST_PROCESSING: 'Обработка',
    BREAK: 'Перерыв',
    LUNCH: 'Обед',
    DND: 'Не беспокоить',
    OFFLINE: 'Офлайн',
  };
  return labels[status] || status || 'Офлайн';
}

export function TelephonyEmployeesPanel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<BrowserPhoneUser[]>([]);
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');
  const [warning, setWarning] = useState('');
  const [error, setError] = useState('');

  const usersQuery = useQuery({
    queryKey: ['telephony-browser-users'],
    queryFn: telephonyApi.users,
    enabled: userHasRole(user, 'ADMIN'),
  });

  const routesQuery = useQuery({
    queryKey: ['telephony-routes'],
    queryFn: telephonyApi.routes,
    enabled: userHasRole(user, 'ADMIN'),
  });

  useEffect(() => {
    if (!usersQuery.data) return;
    setRows(usersQuery.data);
    setDirty(new Set());
  }, [usersQuery.data]);

  const filtered = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return rows;
    return rows.filter(item => [item.full_name, item.email, item.extension]
      .some(part => String(part || '').toLowerCase().includes(value)));
  }, [rows, search]);

  // INTEZ TELEPHONY MANAGED ROUTING V15.7.3
  const groupsByUser = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const group of routesQuery.data?.groups || []) {
      for (const member of group.members || []) {
        const list = map.get(member.portalUserId) || [];
        list.push(group.name);
        map.set(member.portalUserId, list);
      }
    }
    return map;
  }, [routesQuery.data]);

  const stats = useMemo(() => ({
    total: rows.length,
    enabled: rows.filter(item => item.browser_phone_enabled).length,
    inbound: rows.filter(item => item.browser_phone_enabled && item.allow_inbound).length,
    online: rows.filter(item => item.operator_status && item.operator_status !== 'OFFLINE').length,
  }), [rows]);

  const update = (userId: string, patch: Partial<BrowserPhoneUser>) => {
    setRows(current => current.map(item => item.portal_user_id === userId ? { ...item, ...patch } : item));
    setDirty(current => new Set(current).add(userId));
    setMessage('');
    setWarning('');
    setError('');
  };

  const save = useMutation({
    mutationFn: async () => {
      const changed = rows.filter(item => dirty.has(item.portal_user_id));
      for (const item of changed) {
        await telephonyApi.updateUser(item.portal_user_id, {
          extension: item.extension,
          browserPhoneEnabled: item.browser_phone_enabled,
          allowInbound: item.allow_inbound,
          allowOutbound: item.allow_outbound,
          allowTransfer: item.allow_transfer,
          allowRecordings: item.allow_recordings,
        });
      }
      return changed.length;
    },
    onSuccess: async count => {
      setDirty(new Set());
      setError('');
      setWarning('');
      setMessage(count ? `Настройки сотрудников сохранены: ${count}` : 'Изменений нет');
      await Promise.all([
        usersQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ['telephony-browser-config'] }),
      ]);
      window.dispatchEvent(new Event('intez:phone-config-changed'));
    },
    onError: reason => {
      setWarning('');
      setError(reason instanceof Error ? reason.message : 'Не удалось сохранить сотрудников');
    },
  });

  const regenerateCredentials = useMutation({
    mutationFn: (item: BrowserPhoneUser) => telephonyApi.regenerateUserCredentials(item.portal_user_id),
    onSuccess: async result => {
      setError('');
      setWarning(result.registrationRevoked ? '' : result.message);
      setMessage(result.registrationRevoked ? result.message : '');
      await queryClient.invalidateQueries({ queryKey: ['telephony-browser-config'] });
      window.dispatchEvent(new Event('intez:phone-config-changed'));
    },
    onError: reason => {
      setWarning('');
      setError(reason instanceof Error ? reason.message : 'Не удалось перегенерировать SIP-доступ');
    },
    onSettled: async () => {
      await usersQuery.refetch();
    },
  });

  const regenerate = (item: BrowserPhoneUser) => {
    const availability = sipCredentialRegenerationAvailability(item, dirty.size > 0);
    if (!availability.allowed) {
      setMessage('');
      setWarning('');
      setError(availability.reason);
      return;
    }
    const retryRevocation = item.sip_credentials_state === 'REVOCATION_PENDING';
    const confirmation = retryRevocation
      ? `Повторить сброс старой SIP-регистрации для «${item.full_name}»?`
      : `Перегенерировать SIP-доступ для «${item.full_name}»? Телефон сотрудника автоматически переподключится.`;
    if (!window.confirm(confirmation)) return;
    setMessage('');
    setWarning('');
    setError('');
    regenerateCredentials.mutate(item);
  };

  if (!userHasRole(user, 'ADMIN')) {
    return <section className="surface telephony-stage-panel telephony-stage-access">
      <Users />
      <div><h3>Сотрудники телефонии</h3><p>Изменение добавочных и прав доступно администратору.</p></div>
    </section>;
  }

  return <section className="surface telephony-stage-panel telephony-employees-panel">
    <div className="telephony-stage-panel-head">
      <div><span className="telephony-stage-icon"><Users /></span><div><small>ОПЕРАТОРЫ</small><h2>Сотрудники</h2><p>Внутренние номера и доступ к звонкам. Провайдерские SIP-данные здесь не используются.</p></div></div>
      <button type="button" className="button primary" disabled={!dirty.size || save.isPending || regenerateCredentials.isPending} onClick={() => save.mutate()}><Save /> {save.isPending ? 'Сохраняем…' : `Сохранить${dirty.size ? ` · ${dirty.size}` : ''}`}</button>
    </div>

    <div className="telephony-employee-stats">
      <article><Users /><span><small>Сотрудников</small><b>{stats.total}</b></span></article>
      <article><Headphones /><span><small>Телефон включён</small><b>{stats.enabled}</b></span></article>
      <article><PhoneIncoming /><span><small>Принимают входящие</small><b>{stats.inbound}</b></span></article>
      <article><CheckCircle2 /><span><small>Сейчас онлайн</small><b>{stats.online}</b></span></article>
    </div>

    <div className="telephony-employees-toolbar">
      <label className="search-field"><Search /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Сотрудник, email или добавочный" /></label>
      <span>{usersQuery.isFetching ? <><LoaderCircle className="spin" /> Обновляем…</> : `${filtered.length} сотрудников`}</span>
    </div>

    {error && <div className="form-error">{error}</div>}
    {warning && <div className="form-warning"><AlertTriangle /> {warning}</div>}
    {message && <div className="form-success"><CheckCircle2 /> {message}</div>}

    <div className="telephony-employees-table">
      <div className="telephony-employees-head"><span>Сотрудник</span><span>Добавочный</span><span>Телефон</span><span>Группа</span><span>Входящие</span><span>Исходящие</span><span>Устройство / статус</span></div>
      {filtered.map(item => <article key={item.portal_user_id}>
        <span className="telephony-employee-person"><i>{(item.full_name || '?').split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()}</i><span><b>{item.full_name}</b><small>{item.email}</small></span></span>
        <span><input className="telephony-extension-input" disabled={save.isPending || regenerateCredentials.isPending} value={item.extension || ''} onChange={event => update(item.portal_user_id, { extension: event.target.value.replace(/\D/g, '').slice(0, 6) })} placeholder="200" /></span>
        <span><label className="telephony-compact-switch"><input type="checkbox" disabled={save.isPending || regenerateCredentials.isPending} checked={item.browser_phone_enabled} onChange={event => update(item.portal_user_id, { browser_phone_enabled: event.target.checked })} /><i /><b>{item.browser_phone_enabled ? 'Включён' : 'Отключён'}</b></label></span>
        <span><b>{(groupsByUser.get(item.portal_user_id) || [])[0] || '—'}</b><small>{(groupsByUser.get(item.portal_user_id) || []).length > 1 ? `Ещё ${(groupsByUser.get(item.portal_user_id) || []).length - 1}` : (groupsByUser.get(item.portal_user_id) || []).length ? 'Группа вызова' : 'Не участвует'}</small></span>
        <span><label className="telephony-permission-check"><input type="checkbox" checked={item.allow_inbound} disabled={!item.browser_phone_enabled || save.isPending || regenerateCredentials.isPending} onChange={event => update(item.portal_user_id, { allow_inbound: event.target.checked })} /><PhoneIncoming /> Разрешены</label></span>
        <span><label className="telephony-permission-check"><input type="checkbox" checked={item.allow_outbound} disabled={!item.browser_phone_enabled || save.isPending || regenerateCredentials.isPending} onChange={event => update(item.portal_user_id, { allow_outbound: event.target.checked })} /><PhoneOutgoing /> Разрешены</label></span>
        <span className="telephony-device-cell">
          {item.browser_phone_enabled ? <><Headphones /><span><b>Браузер WebRTC</b><small><i className={`status-dot status-${String(item.operator_status || 'OFFLINE').toLowerCase()}`} /> {statusLabel(item.operator_status)}</small>{item.sip_credentials_state === 'ROTATING' && <small className="telephony-credential-state warning">Примените FreeSWITCH в диагностике</small>}{item.sip_credentials_state === 'REVOCATION_PENDING' && <small className="telephony-credential-state warning">Нужно сбросить старую регистрацию</small>}{item.sip_credentials_state === 'REPAIR_REQUIRED' && <small className="telephony-credential-state error">Требуется восстановление FreeSWITCH</small>}</span></> : <><CircleOff /><span><b>Не используется</b><small>Телефон отключён</small></span></>}
          <button
            type="button"
            className="telephony-credential-button"
            title={sipCredentialRegenerationAvailability(item, dirty.size > 0).reason || sipCredentialActionLabel(item)}
            aria-label={`${sipCredentialActionLabel(item)} для ${item.full_name}`}
            disabled={!sipCredentialRegenerationAvailability(item, dirty.size > 0).allowed || regenerateCredentials.isPending || save.isPending}
            onClick={() => regenerate(item)}
          >
            {regenerateCredentials.isPending && regenerateCredentials.variables?.portal_user_id === item.portal_user_id
              ? <LoaderCircle className="spin" />
              : <KeyRound />}
          </button>
        </span>
      </article>)}
      {!usersQuery.isLoading && !filtered.length && <div className="telephony-stage-empty"><Users /><span><b>Сотрудники не найдены</b><small>Измените строку поиска.</small></span></div>}
    </div>
  </section>;
}
